import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Route, Switch, Redirect, useRouteMatch } from 'react-router-dom';
import { connect } from 'react-redux';

import { MonitoringContext } from './monitoring.context';
import { ApplicationsContext } from './application/applications.context';
import { DevicesContext } from './devices/devices.context';
import { LogsContext } from './logs/logs.context';
import { SystemActionsContext } from './systemActions/systemActions.context';

import TabGroup from '../../components/TabGroup/TabGroup';
import PanelPage from './panel/panel.page';
import ApplicationPage from './application/application.page';
import DevicesPage from './devices/devices.page';
import LogsPage from './logs/logs.page';
import SystemActionsPage from './systemActions/systemActions.page';
import LogDetailsPage from './logDetails/logDetails.page';
import MonitoringActions from '../../components/monitoringActions/monitoringActions';
import { actions } from '../../state';
import { config } from '../../api/config';
import { formatFilename, hasPermission } from '../../utils';

import { ButtonDownload, ButtonSimple, HeaderActions } from '../../components';

import styles from './monitoring.module.scss';

const monitoringRoutes = [
  { title: 'Skydelis', route: '/panel', component: PanelPage },
  { title: 'Aplikacija', route: '/application', component: ApplicationPage },
  { title: 'Valdikliai', route: '/devices', component: DevicesPage },
  { title: 'Log\u2019ai', route: '/logs', component: LogsPage },
  {
    title: 'Sistemos veiksmai',
    route: '/system-actions',
    component: SystemActionsPage,
    permission: config.PERMISSIONS.auditLogs.view,
  },
];

const MonitoringPage = ({
  dispatch,
  match,
  location: { pathname },
  xlsLoading,
  permissions,
}) => {
  const visibleRoutes = monitoringRoutes.filter(
    route => !route.permission || hasPermission(route.permission, permissions)
  );
  const isDetails = useRouteMatch('/monitoring/devices/:id');
  const {
    filteredBody,
    dateFromLtFilter,
    setDateFromLtFilter,
    dateToGtFilter,
    setDateToGtFilter,
    locationId,
    setLocationId,
    handleSubmit,
    clearFilters,
  } = useContext(MonitoringContext);

  const {
    filteredBody: applicationsFilteredBody,
    clearFilteredBody: applicationsClearFilteredBody,
  } = useContext(ApplicationsContext);

  const {
    filterBody: devicesFilteredBody,
    clearFilters: devicesClearFilteredBody,
  } = useContext(DevicesContext);

  const {
    filterBody: logsFilteredBody,
    clearFilters: logsClearFilteredBody,
  } = useContext(LogsContext);

  const {
    filterBody: systemActionsFilteredBody,
    clearFilters: systemActionsClearFilteredBody,
  } = useContext(SystemActionsContext);

  const exportXls = () => {
    if (pathname.includes('/logs')) {
      const filename = formatFilename('Logai');
      dispatch(
        actions.monitoring.exportLogsAsyncAction(logsFilteredBody, filename)
      );
    }
  };

  if (isDetails)
    return (
      <Switch>
        <Route path={`${match.url}/devices/:id`} component={LogDetailsPage} />
      </Switch>
    );

  return (
    <div className="monitoring-page">
      <div className={styles.buttonContainer}>
        <TabGroup
          items={visibleRoutes.map(({ route, title }) => ({
            key: route,
            label: title,
            to: `${match.url}${route}`,
            isActive: pathname.includes(route),
          }))}
        />

        {pathname.includes('/panel') && (
          <MonitoringActions
            filteredBody={filteredBody}
            locationId={locationId}
            setLocationId={setLocationId}
            dateFromLtFilter={dateFromLtFilter}
            setDateFromLtFilter={setDateFromLtFilter}
            dateToGtFilter={dateToGtFilter}
            setDateToGtFilter={setDateToGtFilter}
            handleSubmit={handleSubmit}
            clearFilters={clearFilters}
          />
        )}
        {pathname.includes('/application') && (
          <HeaderActions>
            {applicationsFilteredBody && (
              <ButtonSimple
                type="filter"
                onClick={applicationsClearFilteredBody}
              >
                Išvalyti filtrus
              </ButtonSimple>
            )}
            <ButtonDownload onClick={exportXls} loading={xlsLoading} />
          </HeaderActions>
        )}
        {pathname.includes('/logs') && !pathname.includes('/system-actions') && (
          <HeaderActions>
            {logsFilteredBody && (
              <ButtonSimple
                type="filter"
                onClick={() => logsClearFilteredBody()}
              >
                Išvalyti filtrus
              </ButtonSimple>
            )}
            <ButtonDownload onClick={exportXls} loading={xlsLoading} />
          </HeaderActions>
        )}
        {pathname.includes('/system-actions') && (
          <HeaderActions>
            {systemActionsFilteredBody && (
              <ButtonSimple
                type="filter"
                onClick={() => systemActionsClearFilteredBody()}
              >
                Išvalyti filtrus
              </ButtonSimple>
            )}
          </HeaderActions>
        )}
        {pathname.includes('/devices') && devicesFilteredBody && (
          <HeaderActions>
            <ButtonSimple
              type="filter"
              onClick={() => devicesClearFilteredBody()}
            >
              Išvalyti filtrus
            </ButtonSimple>
          </HeaderActions>
        )}
      </div>

      <Switch>
        <Route
          exact
          path={match.url}
          component={() => <Redirect to={`${match.url}/panel`} />}
        />
        {visibleRoutes.map(({ route, component }) => (
          <Route
            key={route}
            path={`${match.url}${route}`}
            component={component}
          />
        ))}
        {pathname.includes('/system-actions') &&
          !hasPermission(config.PERMISSIONS.auditLogs.view, permissions) && (
            <Redirect to={`${match.url}/panel`} />
          )}
      </Switch>
    </div>
  );
};

const mapStateToProps = state => ({
  xlsLoading: state.download.xlsLoading,
  permissions: state.auth.permissions,
});

MonitoringPage.propTypes = {
  dispatch: PropTypes.func.isRequired,
  match: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  permissions: PropTypes.arrayOf(PropTypes.number).isRequired,
};

export default connect(mapStateToProps)(MonitoringPage);
